import {useConfig} from '@aboutyou/config';
import {
  useGrpcRequestMutation,
  useLegacyGrpcRequestCallback,
} from '@aboutyou/data-fetcher';
import {CheckoutV1_getCheckoutUrlLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {Device} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {Device as TadaridaDevice} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import {PaymentService_GetPOAURLLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/payment/service.grpc.lazy';
import {Redirect} from '@aboutyou/router';
import {
  Button,
  COLORS,
  Divider,
  LoadingPageSpinner,
} from '@aboutyou/ui-components';
import {useBasketStateTotalPrice, useUserToken} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {AuthenticatedView} from '../../../features/Authentication/AuthenticatedView/AuthenticatedView';
import {CheckoutErrorView} from '../components/CheckoutErrorView';
import {useGetCheckoutUrlRequestOptions} from '../hooks/useGetCheckoutUrl';

export const DEV_AppCheckoutUrlGenerator = () => {
  const config = useConfig();
  if (config.environment === 'production') {
    return <Redirect to={'/'} />;
  }

  return (
    <AuthenticatedView
      context={'app-checkout-url-page'}
      View={AppCheckoutUrl}
      Fallback={CheckoutErrorView}
    />
  );
};

const AppCheckoutUrl = () => {
  const [loading, setLoading] = useState(false);
  const entryTimestamp = Date.now();
  const params = useGetCheckoutUrlRequestOptions({entryTimestamp});
  const getCOUrl = useLegacyGrpcRequestCallback(CheckoutV1_getCheckoutUrlLazy);
  const basketPrice = useBasketStateTotalPrice();

  const getPoaEntryPoint = useGrpcRequestMutation(
    PaymentService_GetPOAURLLazy,
    {},
  );
  const userToken = useUserToken();

  const handleOnCheckoutClick = async (device: Device, express: boolean) => {
    setLoading(true);

    params.rawConsentConfig = JSON.stringify({
      ga_session_id: 'test-session-id',
      consent: {
        ga: true,
        fr: true,
      },
      app: {
        app_instance_id: 'test-app-id',
        firebase_app_id: 'test-firebase-app-id',
        adid: 'test-adid',
        idfa: 'test-idfa',
        gps_adid: 'test-gps-adid',
        idfv: 'test-idfv',
        android_id: 'test-android-id',
        app_version: '2.2.2',
      },
    });

    if (express) {
      params.expressProvider = {
        $case: 'klarna',
        klarna: {},
      };
      params.basketTotal = basketPrice?.price ?? 0;
    }

    const entryUrl = new URL(
      (await getCOUrl.mutateAsync({...params, device} as any)).entryUrl,
    );
    entryUrl.protocol = window.location.protocol;
    if (entryUrl.protocol === 'http:') {
      entryUrl.hostname = 'app.aboutyou-staging.com.local';
    } else {
      entryUrl.hostname = 'app.aboutyou-staging.com';
    }
    entryUrl.port = window.location.port;
    if (device === Device.ANDROID_APP) {
      entryUrl.searchParams.set('isTwa', 'true');
    }
    entryUrl.searchParams.set('gaTracking', '1');
    window.location.href = entryUrl.toString();
  };

  const handleOnPoaEntryClick = async (device: TadaridaDevice) => {
    setLoading(true);

    const entryUrl = new URL(
      (
        await getPoaEntryPoint.mutateAsync({
          session: {authToken: userToken ?? ''},
          orderId: 123456,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - overriding the default device config to be either android or ios for testing purposes
          config: {
            device,
            clientVersion: '0.0.1',
          },
        })
      ).url,
    );
    // eslint-disable-next-line no-self-assign
    entryUrl.pathname = entryUrl.pathname; // + '/checkout';
    entryUrl.protocol = window.location.protocol;
    if (entryUrl.protocol === 'http:') {
      entryUrl.hostname = 'app.aboutyou-staging.com.local';
    } else {
      entryUrl.hostname = 'app.aboutyou-staging.com';
    }
    entryUrl.port = window.location.port;
    window.location.href = entryUrl.toString();
  };

  return (
    <Wrapper>
      {loading ? <Spinner /> : null}
      {[Device.IOS_APP, Device.ANDROID_APP].map(device => (
        <Button
          key={device}
          onClick={() => handleOnCheckoutClick(device, false)}
        >
          {device === Device.IOS_APP ? 'iOS Checkout' : 'Android Checkout'}
        </Button>
      ))}
      <Divider />
      {[Device.IOS_APP, Device.ANDROID_APP].map(device => (
        <Button
          key={device}
          onClick={() => handleOnCheckoutClick(device, true)}
          variant="secondary"
        >
          {device === Device.IOS_APP
            ? 'iOS Express Checkout'
            : 'Android Express Checkout'}
        </Button>
      ))}
      <Divider />
      {[TadaridaDevice.IOS_APP, TadaridaDevice.ANDROID_APP].map(device => (
        <Button
          key={device}
          onClick={() => handleOnPoaEntryClick(device)}
          variant="secondary"
        >
          {device === TadaridaDevice.IOS_APP
            ? 'iOS PoA entry'
            : 'Android PoA entry'}
        </Button>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  height: 800px;
  justify-content: center;
  background: ${COLORS['Grey.Shade300']};
`;
const Spinner = styled(LoadingPageSpinner)`
  z-index: 1;
  position: fixed;
  height: 100%;
  background: #e2e2e2c2;
  left: 0;
`;
